import { h, JSX } from 'preact'
import { Message } from '../../../../lib/intl/Message'
import {
  FremmoteInfo,
  Geografi,
  GeografiMedStemmeinfo,
  MandatInfo,
  OpptaltInfo,
  PartiResultat,
  StemmeInfo
} from '../../../domain'
import {
  calculateStemmerForTapeSisteMandat,
  calculateStemmerForVinneNesteMandat,
  hasNesteMandat,
  hasSisteMandat,
  isTrygg,
  validateListestemmer
} from './tapeVinne'

import { withSectionErrorBoundary } from '../../shared/SectionErrorBoundary/SectionErrorBoundary'
import { Close } from './illustrations/close'
import { Safe } from './illustrations/safe'

import s from './Mandatkamp.module.css'
import { useWidgetUrls } from '../../../routes/widget'
import { useCustomContext } from '../../../../lib/context'
import { idFrom } from '../../../api'

function isVisible(partiResultat: PartiResultat) {
  return (
    partiResultat.parti.id !== 'BLANKE' &&
    (partiResultat.parti.kategori === 1 ||
      partiResultat.parti.kategori === 2 ||
      partiResultat.parti.kategori === 3)
  )
}

type Props = {
  geografi: GeografiMedStemmeinfo
  valgtype: 'fylke' | 'kommune'
  partier: PartiResultat[]
  opptalt?: OpptaltInfo
  mandater?: MandatInfo
  frammote?: FremmoteInfo
  stemmer?: StemmeInfo
  widgetFooter?: JSX.Element
}

function _Mandatkamp(props: Props) {
  const { geografi, valgtype, partier, mandater, stemmer, opptalt, frammote, widgetFooter } = props
  const { mandatkampUrl } = useWidgetUrls()
  const { sendError } = useCustomContext()

  if (!stemmer || !frammote || !frammote.prosent || !geografi.stemmeberettigede || !mandater) {
    return null
  }

  const visiblePartier = partier.filter(isVisible)
  const sisteParti = visiblePartier.find(hasSisteMandat(mandater))
  const nesteParti = visiblePartier.find(hasNesteMandat(mandater))

  if (nesteParti === undefined || sisteParti === undefined) {
    return null
  }

  if (sisteParti.parti.id === nesteParti.parti.id) {
    return null
  }

  if (nesteParti.parti.id !== mandater.nesteMandat) {
    throw new Error(
      `Neste parti stemmer ikke overens med info fra Valgdir: ${mandater.nesteMandat} - ${nesteParti.parti.id}`
    )
  }

  if (sisteParti.parti.id !== mandater.sisteMandat) {
    throw new Error(
      `Siste parti stemmer ikke overens med info fra Valgdir: ${mandater.sisteMandat} - ${sisteParti.parti.id}`
    )
  }

  if (
    typeof nesteParti.mandater === 'undefined' ||
    typeof sisteParti.mandater === 'undefined' ||
    typeof sisteParti.mandater.sisteMandat === 'undefined'
  ) {
    throw Error('`sisteParti` eller `nesteParti` mangler mandatinfo')
  }

  const brukListestemmer = valgtype === 'kommune' && !!opptalt && opptalt.oppgjor === 100
  if (
    typeof sisteParti.stemmer.antall !== 'undefined' &&
    typeof nesteParti.stemmer.antall !== 'undefined'
  ) {
    /* Kan kun validere brukListestemmer når valgdir leverer antall stemmer for partiene.
     * Validering blir ikke gjort når valgdir leverer prognose (uten antall)
     * */
    const validation = validateListestemmer(sisteParti, brukListestemmer)
    if (!validation.valid) {
      sendError(
        `Feil ved validering av listestemmer i ${geografi.navn.nb}: ${validation.error} \n Skjuler komponenten.`
      )
      return null
    }
  }

  const vinneStemmer = calculateStemmerForVinneNesteMandat(
    sisteParti,
    nesteParti,
    brukListestemmer,
    mandater.antall
  )

  const tapeStemmer = calculateStemmerForTapeSisteMandat(
    sisteParti,
    nesteParti,
    brukListestemmer,
    mandater.antall
  )

  const endringStemmer = Math.min(vinneStemmer, tapeStemmer)
  const trygg = isTrygg(geografi.stemmeberettigede, frammote.prosent, stemmer.total, endringStemmer)
  const ferdigOpptalt = !!opptalt && opptalt.forelopig >= 100

  return (
    <section
      class={s.mandatkamp}
      data-widget-url={mandatkampUrl(geografi, valgtype)}
      data-valgtype={valgtype}
      data-grunndata={idFrom(geografi)}
      data-modulnavn="mandatkamp"
    >
      <h3>
        <Message
          id={`mandatkamp-${valgtype}-tittel`}
          modifiers={{ opptalt: ferdigOpptalt }}
          values={{ navn: geografi.navn }}
        />
      </h3>
      <p>
        <Message
          id={`mandatkamp-${valgtype}-${trygg ? 'trygg' : 'utrygg'}`}
          values={{ inneparti: sisteParti.parti.navn, uteparti: nesteParti.parti.navn }}
          modifiers={{ opptalt: ferdigOpptalt }}
        />
      </p>
      <div aria-hidden="true">
        {trygg ? (
          <Safe sisteParti={sisteParti.parti} nesteParti={nesteParti.parti} />
        ) : (
          <Close sisteParti={sisteParti.parti} nesteParti={nesteParti.parti} />
        )}
      </div>
      {widgetFooter}
    </section>
  )
}

export const Mandatkamp = withSectionErrorBoundary(_Mandatkamp)
